/**
 * This enum is copied from PLMVisWeb for [PLMVisWeb.BBoxSurfaceScanner.WaitForPassSeriesEndMode]
 */
const WaitForPassSeriesEndMode = {
    /** Wait for a pass-series to finish, even if a pass-series hasn't even begun yet */
    WAIT_EVEN_IF_NOT_STARTED_YET: 0,
    /**
     * Wait for a pass-series to finish only if a pass-series is already scheduled or
     * one is actually in-progress.  Otherwise, call the callback immediately.
     */
    WAIT_EVEN_IF_ONLY_SCHEDULED: 1,
    /**
     * Wait for a pass-series to finish only if one is already in-progress.
     * Otherwise, call the callback immediately.
     */
    WAIT_ONLY_IF_ONGOING: 2
};

/* eslint-disable no-console */
/**
 * @param {object} _graphicsLoader - The graphics loader instance
 * @class
 * @classdesc Culling class function
 */
const Culling = function (_graphicsLoader) {
    /**
     * PLMVisWeb Viewer instance
     *
     * @type {object}
     * @ignore
     */
    var m_viewer = _graphicsLoader.m_viewManager;

    /**
     * CVFLoader instance
     *
     * @type {object}
     * @ignore
     */
    var m_atlasCVFLoader = _graphicsLoader.m_atlasCVFLoader;

    /**
     * Culling enable status
     *
     * @type {boolean}
     * @ignore
     */
    var m_enabled = false;

    /**
     * Event listeners. <key: event name, value: event array>
     *
     * @type {object}
     * @ignore
     */
    var m_eventListeners = undefined;

    /**
     * PLMVisWeb bboxSS Object
     *
     * @type {object}
     * @ignore
     */
    var m_bboxSS = undefined;

    /**
     * Culling settings, see DefaultCullingOptions and PLMVisWeb bboxSurfaceScanner for more detail
     *
     * @type {object}
     * @ignore
     */
    var m_cullingOptions = undefined;

    /**
     * BboxSS psId Set
     *
     * @type {Set<string>}
     * @ignore
     */
    var m_psIdSet = new Set();

    /**
     * Transmitted psId Set
     *
     * @type {Set<string>}
     * @ignore
     */
    var m_transmittedPsIdSet = new Set();

    /**
     * BboxSS callback
     *
     * @type {Function}
     * @ignore
     */
    var m_bboxSSCallback = undefined;

    /**
     * Flag to indicate whether NotBusy event invoked in current pass
     *
     * @type {boolean}
     * @ignore
     */
    var m_notBusyInvoked = false;

    /**
     * @description Enable culling
     * @param {object} options - The options (Optional)
     * @param {string} options.type - The type of culling, default = "bboxSurfaceScanner"
     * @param {number} options.batchSize - The size of the batch, default = -1 (no batching)
     * @param {object} options.scannerOptions - PLMVisWeb scanner options
     * @param {boolean} options.debug - Culling debug flag
     * @param {boolean} options.filterUsingPartContext - Culling filterUsingPartContext flag
     * @param {boolean} options.includeXformInBatchLoad - Culling includeXformInBatchLoad flag
     */
    Culling.prototype.enable = function (options) {
        m_enabled = true;
        this.reset();
        m_bboxSS = m_viewer.bboxSurfaceScanner;

        if (options !== undefined && typeof options === 'object') {
            this.setCullingOptions(options);
        }

        registerLoaderEvent();
        m_bboxSS.registerPassInfoEvent(busyEvent);
    };

    /**
     * @description Disable culling
     */
    Culling.prototype.disable = function () {
        m_eventListeners = undefined;
        this.reset();
        unregisterLoaderEvent();
        if (this.isEnabled() && m_bboxSS !== undefined) {
            m_bboxSS = undefined;
        }
        m_enabled = false;
    };

    /**
     * @description Get PLMVisWeb scanner
     * @returns {object} - PLMVisWeb scanner
     */
    Culling.prototype.getScanner = function () {
        return m_bboxSS;
    };

    /**
     * @description Whether Culling is enabled or not
     * @returns {boolean} - Enable status
     */
    Culling.prototype.isEnabled = function () {
        return m_enabled;
    };

    /**
     * @description Notify bboxSS when the graphics has finished.
     */
    Culling.prototype.onEndPartGeometryLoad = function () {
        if (!m_enabled) {
            return;
        }

        if (m_bboxSSCallback !== undefined && typeof m_bboxSSCallback === 'function') {
            m_bboxSSCallback();
        }

        if (m_bboxSS !== undefined) {
            if (m_bboxSS.isActive === true && m_psIdSet.size > 0) {
                console.warn(`Culling Warning: Unloaded part count: ${m_psIdSet.size}.`);

                dispatchEvent(CullingEventType.batchFailure, Array.from(m_psIdSet));

                m_psIdSet.clear();
                m_bboxSSCallback = undefined;
            }

            // Don't invoke NotBusy Event for the first batch since there is only product structure in the scene graph.
            //
            if (m_transmittedPsIdSet.size > 0) {
                m_bboxSS.waitForPassSeriesEnd(WaitForPassSeriesEndMode.WAIT_EVEN_IF_NOT_STARTED_YET, () => {
                    if (m_notBusyInvoked === false) {
                        m_notBusyInvoked = true;
                        dispatchEvent(CullingEventType.notBusy);
                    }
                });
            }
        }
    };

    /**
     * @description Register CullingEvent
     * @example
     *
let cullingManager = m_atlasGraphicsLoader.getCullingManager();
cullingManager.addEventListener("batchLoad", async (ObjectIds) => {
    // Send API to Atlas to ask graphics for these psIds
    await sendApi(api);
});
     * @param {string} eventName - Event name
     * @param {Function} listener - Listener function
     */
    Culling.prototype.addEventListener = function (eventName, listener) {
        // Ignore invalid event name
        if (eventName === undefined || !CullingEventType.hasOwnProperty(eventName)) {
            return;
        }

        if (m_eventListeners === undefined) {
            m_eventListeners = {};
        }

        if (m_eventListeners[eventName] === undefined) {
            m_eventListeners[eventName] = [];
        }

        if (m_eventListeners[eventName].indexOf(listener) === -1) {
            m_eventListeners[eventName].push(listener);
        }
    };

    /**
     * @description Remove CullingEvent
     * @example
     *
let cullingManager = m_atlasGraphicsLoader.getCullingManager();
cullingManager.removeEventListener("batchLoad", event);
     * @param {string} eventName - Event name
     * @param {Function} listener - Listener function
     */
    Culling.prototype.removeEventListener = function (eventName, listener) {
        // Ignore invalid event name
        if (eventName === undefined || !CullingEventType.hasOwnProperty(eventName)) {
            return;
        }

        if (m_eventListeners === undefined) {
            m_eventListeners = {};
        }

        if (m_eventListeners[eventName] === undefined) {
            m_eventListeners[eventName] = [];
        }

        if (listener === undefined) {
            m_eventListeners[eventName] = [];
        } else {
            let index = m_eventListeners[eventName].indexOf(listener);

            if (index !== -1) {
                m_eventListeners[eventName].splice(index, 1);
            }
        }
    };

    /**
     * @description Set culling options, override bboxSS options.
     * @param {object} options - The options object
     * @param {string} options.type - The type of culling, default = "bboxSurfaceScanner"
     * @param {number} options.batchSize - The size of the batch, default = -1 (no batching)
     * @param {object} options.scannerOptions - PLMVisWeb scanner options
     * @param {boolean} options.debug - Culling debug flag
     * @param {boolean} [options.filterUsingPartContext=false] - Flag to suppress occs in the same context.
     * @param {boolean} [options.includeXformInBatchLoad=true] - Flag to include occ transform in the BatchLoad event.
     * @param {boolean} [options.includeEmptyBBoxNodes=true] - Flag to include empty bbox nodes in the callback.
     * @param {boolean} [updateOnly=false] - Whether current options to update existing culling options
     */
    Culling.prototype.setCullingOptions = function (options, updateOnly = false) {
        if (updateOnly === false || m_cullingOptions === undefined) {
            m_cullingOptions = DefaultCullingOptions;
        }

        if (options !== undefined && typeof options === 'object') {
            filterOptions(options, m_cullingOptions);
        }

        if (debugEnabled()) {
            console.log('=== Culling Diagnostics === \n  Current Culling options:', m_cullingOptions);
        }

        const setObjValueRecursive = function (currentObj, targetObj) {
            if (currentObj === undefined || targetObj === undefined || typeof currentObj !== 'object' || typeof targetObj !== 'object') {
                return;
            }
            for (let key in currentObj) {
                if (currentObj.hasOwnProperty(key)) {
                    let value = currentObj[key];
                    if (typeof value === 'object') {
                        if (targetObj[key] !== undefined) {
                            setObjValueRecursive(value, targetObj[key]);
                        } else {
                            console.warn(`Invalid Culling setting: ${key}: ${value}.`);
                        }
                    } else {
                        targetObj[key] = value;
                    }
                } else {
                    console.warn(`Invalid Culling setting: ${key}.`);
                }
            }
        };

        setObjValueRecursive(m_cullingOptions.scannerOptions, m_bboxSS);
    };

    /**
     * @description Reset Culling status, usually used when assembly context has changed.
     */
    Culling.prototype.reset = function () {
        if (!m_enabled) {
            return;
        }

        resetBatch();
        m_notBusyInvoked = false;
    };

    const busyEvent = function () {
        m_notBusyInvoked = false;
    };

    const unregisterLoaderEvent = function () {
        m_atlasCVFLoader.unregisterQueryInterface();
        m_atlasCVFLoader.removeEventListener('partsLoaded', partsLoadedHandler);
    };

    const registerLoaderEvent = function () {
        let register = {
            batchLoad: function (objsToLoad, objsToCancel, callback) {
                let hasLoadCandidate = false;

                if (Array.isArray(objsToLoad) && objsToLoad.length > 0) {
                    hasLoadCandidate = true;
                    let eventArray = []; // Each element is the array of part instance psId that has the same prototype.
                    let protoCount = 0; // Batching variable.

                    let objsToLoadSet = new Set(); // Set to deduplicate psId.
                    for (let objToLoad of objsToLoad) {
                        let psId = objToLoad.psId;

                        // Skip if psId is already tracked or already handled in the loop.
                        if (objsToLoadSet.has(psId) || m_psIdSet.has(psId) || m_transmittedPsIdSet.has(psId)) {
                            continue;
                        }

                        if (protoCount === 0) {
                            eventArray.push([]);
                        }
                        // Get all the part instances that share the same prototype.
                        // This way we will transmit the instance override status from the server properly.
                        let instanceIds = getAllInstanceIdsByPsId(psId);

                        // Skip empty nodes based on settings
                        //
                        if (m_cullingOptions.includeEmptyBBoxNodes === false && m_viewer.getBoundingBoxByPsId(psId) === null) {
                            setPartLoadedByPsId(psId);
                            continue;
                        }

                        for (let instanceId of instanceIds) {
                            if (!objsToLoadSet.has(instanceId) && !m_psIdSet.has(instanceId) && !m_transmittedPsIdSet.has(instanceId)) {
                                m_psIdSet.add(instanceId);
                                objsToLoadSet.add(instanceId);
                                m_transmittedPsIdSet.add(instanceId);

                                // Get refset property
                                let refsetName = '';
                                if (m_viewer.properties !== undefined && m_viewer.properties.getProperty instanceof Function) {
                                    // <TODO> Use hardcode "ReferenceSet" property here until PLMVisWeb has better refset solution
                                    //
                                    refsetName = m_viewer.properties.getProperty(instanceId, 'ReferenceSet');
                                }

                                let eventItem = {
                                    psId: instanceId,
                                    refset: refsetName
                                };
                                if (m_cullingOptions.includeXformInBatchLoad === true) {
                                    eventItem.xform = m_viewer.getMatrixByPsId(instanceId, true, false);
                                }
                                eventArray[eventArray.length - 1].push(eventItem);
                            }
                        }
                        protoCount += 1;
                        if (m_cullingOptions.batchSize > 0) {
                            protoCount %= m_cullingOptions.batchSize;
                        }
                    }

                    if (m_cullingOptions.filterUsingPartContext === true) {
                        // Filter out part occs which has the same prototype context
                        for (let idx = 0; idx < eventArray.length; idx++) {
                            let uniqueSet = new Set();
                            let filterEventItem = [];
                            for (let item of eventArray[idx]) {
                                let key = JSON.stringify({
                                    protoId: m_viewer.getProtoIdByPsId(item.psId),
                                    refset: item.refset
                                });
                                if (!uniqueSet.has(key)) {
                                    uniqueSet.add(key);
                                    filterEventItem.push(item);
                                }
                            }
                            eventArray[idx] = filterEventItem;
                        }
                    }

                    if (Array.isArray(eventArray) && eventArray.length > 0) {
                        dispatchEvent(CullingEventType.batchLoad, eventArray);
                    }

                    m_bboxSSCallback = callback;
                }

                if (Array.isArray(objsToCancel) && objsToCancel.length > 0) {
                    for (let psId of objsToCancel) {
                        m_psIdSet.delete(psId);
                        // m_transmittedPsIdSet.delete(psId); // TODO: Investigate why remove this will cause infinite query.
                    }
                    dispatchEvent(CullingEventType.batchCancel, objsToCancel);
                }

                // If there is nothing to load, invoke callback right away.
                if (!hasLoadCandidate) {
                    callback();
                }
            }
        };
        m_atlasCVFLoader.registerQueryInterface(register);

        m_atlasCVFLoader.addEventListener('partsLoaded', partsLoadedHandler);
    };

    const setPartLoadedByPsId = function (psId) {
        // Mark the part instance node as Loaded Override, so bboxSS will not wait for the geometry to be loaded
        // UNLOADED: 0, LOADING: 1, LOADED: 2
        //
        // PLMVisWeb 1.7.3-264 or later
        if (m_viewer.getLoaderDataById !== undefined && m_viewer.getLoaderDataById instanceof Function) {
            let loaderData = m_viewer.getLoaderDataById(psId);
            if (loaderData !== null && loaderData !== undefined) {
                loaderData.loadOverride = 2;
            } else {
                console.warn(`PSID: ${psId} has no associated part node.`);
            }
        }
    };

    const partsLoadedHandler = function (event) {
        let psIds = event.psIds;
        if (psIds === null || psIds === undefined || !(psIds instanceof Array)) {
            console.warn(`Loader event error, ${psIds} is not valid`);
            return;
        }

        if (debugEnabled()) {
            let debugFilterResult = psIds.filter((psId) => m_psIdSet.has(psId));
            if (debugFilterResult.length > 0) {
                console.log('=== Culling Diagnostics === \n  Mark *loadOverride* parts:', { psIds: debugFilterResult });
            }
        }

        for (let psId of psIds) {
            if (m_psIdSet.has(psId)) {
                setPartLoadedByPsId(psId);
                m_psIdSet.delete(psId);
            }
        }
    };

    const resetBatch = function () {
        m_psIdSet.clear();
        m_transmittedPsIdSet.clear();
        m_bboxSSCallback = undefined;
    };

    // <TODO>
    // Remove PLMVisWeb private API to query all instances psId given the psId.
    //
    const getAllInstanceIdsByPsId = function (psId) {
        if (psId === null || psId === undefined) {
            return [];
        }
        let type = m_viewer.getObjectTypeByPsId(psId);
        if (type !== 'Part') {
            return [];
        }
        let instancesPsIds = [];
        // PLMVisWeb 1.7.3-264 or later
        if (m_viewer.getInstanceIdsById !== undefined && m_viewer.getInstanceIdsById instanceof Function) {
            instancesPsIds = m_viewer.getInstanceIdsById(psId);
        }

        return instancesPsIds;
    };

    const dispatchEvent = function (eventName, ...args) {
        if (eventName === null || eventName === undefined) {
            return;
        }

        if (m_eventListeners === undefined || typeof m_eventListeners !== 'object') {
            return;
        }

        let listeners = m_eventListeners[eventName];
        if (listeners === undefined || !(listeners instanceof Array)) {
            return;
        }

        if (debugEnabled()) {
            console.log(`=== Culling Diagnostics === \n  ${eventName}: `, ...args);
        }

        for (let listener of listeners) {
            listener(...args);
        }
    };

    const debugEnabled = function () {
        if (m_cullingOptions !== undefined && m_cullingOptions !== null) {
            return m_cullingOptions.debug === true;
        }
        return false;
    };

    // Filter options based on the default setting KV pair.
    const filterOptions = function (originalOpt, targetOpt) {
        for (const [key, value] of Object.entries(DefaultCullingOptions)) {
            if (originalOpt[key] !== undefined) {
                targetOpt[key] = originalOpt[key];
            }
        }
    };
};

const DefaultCullingOptions = {
    type: 'bboxSurfaceScanner',
    batchSize: 0,
    debug: false,
    filterUsingPartContext: false,
    includeXformInBatchLoad: true,
    includeEmptyBBoxNodes: true,
    // PLMVisWeb scanner options.
    scannerOptions: {
        showBBoxes: true,
        autoScanning: true,
        autoScanDelayMS: 200,
        minDimPx: 100,
        minPartBoundingSphereSize: 1,
        unloadOccluded: false,
        movingFrameSleepMS: 200,
        renderBBoxesOnMovingFrames: true
    },
    movingFrame: {
        hideSmallParts: false
    }
};

export const CullingEventType = Object.freeze({
    batchLoad: 'batchLoad',
    batchCancel: 'batchCancel',
    batchFailure: 'batchFailure',
    notBusy: 'notBusy'
});

export { Culling, WaitForPassSeriesEndMode };
